import { zodResolver } from '@hookform/resolvers/zod'
import { addHours, format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { CalendarIcon, Plus } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { cn } from '../../../../../lib/utils'
import { Button } from '../../../../components/ui/button'
import { Calendar } from '../../../../components/ui/calendar'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../../../../components/ui/form'
import { Input } from '../../../../components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '../../../../components/ui/popover'
import { ScrollArea, ScrollBar } from '../../../../components/ui/scroll-area'
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '../../../../components/ui/select'
import { Textarea } from '../../../../components/ui/textarea'
import { ToggleGroup, ToggleGroupItem } from '../../../../components/ui/toggle-group'
import { useToast } from '../../../../hooks'
import { SessionFormData, sessionSchema } from '../../../../validators/session-validator'
import { createSession } from '../actions/create-session'
import { LoadEmployees } from '../actions/load-employees'
import { colors } from './utils'

interface FormSessionProps {
  updateSessions: () => void
  updateRomms?: () => void
  rooms?: any
}

export function FormSession({ updateSessions, rooms }: FormSessionProps) {
  const [modal, setModal] = useState(false)
  const { showToast } = useToast()
  const { data: employees } = LoadEmployees()

  //const { data: members } = LoadMembers()

  const openModal = () => setModal(true)
  const closeModal = () => {
    form.reset()
    setModal(false)
  }

  const form = useForm<SessionFormData>({
    resolver: zodResolver(sessionSchema),
    defaultValues: {}
  })

  const onSubmit = async (data: SessionFormData) => {
    if (data.inicioAula > data.fimAula) {
      showToast('a data da aula final deve ser maior que a aula inicial', 'error')
      return
    }

    if (data.inicioAula < new Date()) {
      showToast('a data da aula deve ser maior que a data atual', 'error')
      return
    }

    await createSession(data)
    form.reset()
    updateSessions()
    closeModal()
  }

  useEffect(() => {
    if (form.getValues().inicioAula) {
      form.setValue('fimAula', addHours(new Date(form.getValues().inicioAula), 1))
    }
  }, [form.watch('inicioAula')])

  return (
    <div className="">
      <Button className="text-muted bg-acelerafit-green-400 hover:bg-acelerafit-green-200 flex gap-2 " onClick={openModal}>
        <Plus />
        Aula
      </Button>
      {modal && (
        <div className="fixed w-full h-full flex justify-center items-center z-10 bg-[#0000004b] left-0 top-0 ">
          <div className="flex items-center justify-center w-[800px] bg-white shadow-md p-6 rounded-md">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="w-full flex flex-col m-auto gap-4">
                <h3 className="text-3xl text-acelerait-black">Adicionar aula</h3>

                <FormField
                  control={form.control}
                  name="salaId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        <span className="text-gray-400">*</span> Sala
                      </FormLabel>
                      <FormControl>
                        <Select onValueChange={field.onChange} value={field.value?.toString() || ''}>
                          <SelectTrigger className="">
                            <SelectValue placeholder="Selecione" className="w-full" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectGroup>
                              <SelectLabel>salas</SelectLabel>
                              {rooms?.data.data.content.map((item: any) => {
                                return (
                                  <SelectItem value={item.id.toString()} className="" key={item.id}>
                                    <div className="flex gap-2">{item.nome}</div>
                                  </SelectItem>
                                )
                              })}
                            </SelectGroup>
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage className="text-xs">{form.formState.errors.salaId?.message}</FormMessage>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="cor"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        <span className="text-gray-400">*</span> Cor para identificar
                      </FormLabel>
                      <FormControl>
                        <Select onValueChange={field.onChange} value={field.value || ''}>
                          <SelectTrigger className="">
                            <SelectValue placeholder="Selecione" className="w-full" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectGroup>
                              <SelectLabel>cores</SelectLabel>
                              {colors.map((item) => {
                                return (
                                  <SelectItem value={item.color} className="" key={item.id}>
                                    <div className="flex gap-2">
                                      <div className="h-4 w-4 rounded" style={{ backgroundColor: item.color }}></div>
                                      {item.name}
                                    </div>
                                  </SelectItem>
                                )
                              })}
                            </SelectGroup>
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage className="text-xs">{form.formState.errors.nome?.message}</FormMessage>
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-2 gap-3 w-full">
                  <FormField
                    control={form.control}
                    name="nome"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          <span className="text-gray-400">*</span> Nome da Aula
                        </FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Nome da Aula" />
                        </FormControl>
                        <FormMessage className="text-xs">{form.formState.errors.nome?.message}</FormMessage>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="descricao"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          <span className="text-gray-400"></span> Descrição
                        </FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Descrição" />
                        </FormControl>
                        <FormMessage className="text-xs">{form.formState.errors.descricao?.message}</FormMessage>
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <FormField
                    control={form.control}
                    name="inicioAula"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel>Selecione a data de inicio.</FormLabel>
                        <FormControl>
                          <Popover>
                            <PopoverTrigger asChild>
                              <FormControl>
                                <Button variant={'outline'} className={cn('w-full pl-3 text-left font-normal', !field.value && 'text-muted-foreground')}>
                                  {field.value ? format(field.value, 'dd/MM/yyyy HH:mm') : <span>selecione a data e hora</span>}
                                  <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                </Button>
                              </FormControl>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0">
                              <div className="sm:flex">
                                <Calendar mode="single" selected={new Date(field.value)} onSelect={field.onChange} initialFocus locale={ptBR} fromDate={new Date()} />
                                <div className="flex flex-col sm:flex-row sm:h-[300px] divide-y sm:divide-y-0 sm:divide-x">
                                  <ScrollArea className="w-64 sm:w-auto">
                                    <div className="flex sm:flex-col p-2">
                                      {Array.from({ length: 24 }, (_, i) => i)
                                        .reverse()
                                        .map((hour) => (
                                          <Button
                                            key={hour}
                                            size="icon"
                                            variant={field.value && new Date(field.value).getHours() === hour ? 'default' : 'ghost'}
                                            className="sm:w-full shrink-0 aspect-square"
                                            onClick={() => {
                                              field.onChange(new Date(field.value || new Date()).setHours(hour))
                                              console.log(new Date(field.value))
                                            }}
                                          >
                                            {hour}
                                          </Button>
                                        ))}
                                    </div>
                                    <ScrollBar orientation="horizontal" className="sm:hidden" />
                                  </ScrollArea>
                                  <ScrollArea className="w-64 sm:w-auto">
                                    <div className="flex sm:flex-col p-2">
                                      {Array.from({ length: 12 }, (_, i) => i * 5).map((minute) => (
                                        <Button
                                          key={minute}
                                          size="icon"
                                          variant={field.value && new Date(field.value).getMinutes() === minute ? 'default' : 'ghost'}
                                          className="sm:w-full shrink-0 aspect-square"
                                          onClick={() => field.onChange(new Date(field.value || new Date()).setMinutes(minute))}
                                        >
                                          {minute.toString().padStart(2, '0')}
                                        </Button>
                                      ))}
                                    </div>
                                    <ScrollBar orientation="horizontal" className="sm:hidden" />
                                  </ScrollArea>
                                </div>
                              </div>
                            </PopoverContent>
                          </Popover>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="fimAula"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel>Selecione a data do fim.</FormLabel>
                        <FormControl>
                          <Popover>
                            <PopoverTrigger asChild>
                              <FormControl>
                                <Button variant={'outline'} className={cn('w-full pl-3 text-left font-normal', !field.value && 'text-muted-foreground')}>
                                  {field.value ? format(field.value, 'dd/MM/yyyy HH:mm') : <span>selecione a data e hora</span>}
                                  <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                </Button>
                              </FormControl>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0">
                              <div className="sm:flex">
                                <Calendar mode="single" selected={new Date(field.value)} onSelect={field.onChange} initialFocus locale={ptBR} fromDate={new Date()}
                                />
                                <div className="flex flex-col sm:flex-row sm:h-[300px] divide-y sm:divide-y-0 sm:divide-x">
                                  <ScrollArea className="w-64 sm:w-auto">
                                    <div className="flex sm:flex-col p-2">
                                      {Array.from({ length: 24 }, (_, i) => i)
                                        .reverse()
                                        .map((hour) => (
                                          <Button
                                            key={hour}
                                            size="icon"
                                            variant={field.value && new Date(field.value).getHours() === hour ? 'default' : 'ghost'}
                                            className="sm:w-full shrink-0 aspect-square"
                                            onClick={() => {
                                              field.onChange(new Date(field.value || new Date()).setHours(hour))
                                              console.log(new Date(field.value))
                                            }}
                                          >
                                            {hour}
                                          </Button>
                                        ))}
                                    </div>
                                    <ScrollBar orientation="horizontal" className="sm:hidden" />
                                  </ScrollArea>
                                  <ScrollArea className="w-64 sm:w-auto">
                                    <div className="flex sm:flex-col p-2">
                                      {Array.from({ length: 12 }, (_, i) => i * 5).map((minute) => (
                                        <Button
                                          key={minute}
                                          size="icon"
                                          variant={field.value && new Date(field.value).getMinutes() === minute ? 'default' : 'ghost'}
                                          className="sm:w-full shrink-0 aspect-square"
                                          onClick={() => field.onChange(new Date(field.value || new Date()).setMinutes(minute))}
                                        >
                                          {minute.toString().padStart(2, '0')}
                                        </Button>
                                      ))}
                                    </div>
                                    <ScrollBar orientation="horizontal" className="sm:hidden" />
                                  </ScrollArea>
                                </div>
                              </div>
                            </PopoverContent>
                          </Popover>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={form.control}
                  name="diasDaSemana"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        <span className="text-gray-400">*</span> Dias da semana
                      </FormLabel>
                      <FormControl>
                        <ToggleGroup type="multiple" onValueChange={field.onChange} value={field.value} className="w-full flex justify-start">
                          <ToggleGroupItem className="data-[state=on]:bg-acelerafit-green-200 data-[state=on]:text-white" value="0" aria-label="Toggle">
                            Domingo
                          </ToggleGroupItem>
                          <ToggleGroupItem className="data-[state=on]:bg-acelerafit-green-200 data-[state=on]:text-white" value="1" aria-label="Toggle">
                            Segunda
                          </ToggleGroupItem>
                          <ToggleGroupItem className="data-[state=on]:bg-acelerafit-green-200 data-[state=on]:text-white" value="2" aria-label="Toggle">
                            Terça
                          </ToggleGroupItem>
                          <ToggleGroupItem className="data-[state=on]:bg-acelerafit-green-200 data-[state=on]:text-white" value="3" aria-label="Toggle">
                            Quarta
                          </ToggleGroupItem>
                          <ToggleGroupItem className="data-[state=on]:bg-acelerafit-green-200 data-[state=on]:text-white" value="4" aria-label="Toggle">
                            Quinta
                          </ToggleGroupItem>
                          <ToggleGroupItem className="data-[state=on]:bg-acelerafit-green-200 data-[state=on]:text-white" value="5" aria-label="Toggle">
                            Sexta
                          </ToggleGroupItem>
                          <ToggleGroupItem className="data-[state=on]:bg-acelerafit-green-200 data-[state=on]:text-white" value="6" aria-label="Toggle">
                            Sabado
                          </ToggleGroupItem>
                        </ToggleGroup>
                      </FormControl>
                      <FormMessage className="text-xs">{form.formState.errors.professor?.message}</FormMessage>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="professor.id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        <span className="text-gray-400">*</span> Professor
                      </FormLabel>
                      <FormControl>
                        <Select onValueChange={field.onChange} value={field.value?.toString() || ''}>
                          <SelectTrigger className="">
                            <SelectValue placeholder="Selecione" className="w-full" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectGroup>
                              <SelectLabel>professores</SelectLabel>
                              {employees.map((item) => {
                                return (
                                  <SelectItem value={item.id.toString()} className="" key={item.id}>
                                    <div className="flex gap-2">{item.nome}</div>
                                  </SelectItem>
                                )
                              })}
                            </SelectGroup>
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage className="text-xs">{form.formState.errors.professor?.message}</FormMessage>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="observacao"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        <span className="text-gray-400"></span> Observações
                      </FormLabel>
                      <FormControl>
                        <Textarea {...field} placeholder="Observações" className="resize-none" />
                      </FormControl>
                      <FormMessage className="text-xs">{form.formState.errors.nome?.message}</FormMessage>
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-[1fr_80px] items-center justify-center gap-2">
                  <Button type="submit" className="bg-acelerafit-green-200 hover:bg-acelerafit-green-50 text-muted ">
                    Enviar
                  </Button>
                  <Button onClick={closeModal} variant={'outline'}>
                    Fechar
                  </Button>
                </div>
              </form>
            </Form>
          </div>
        </div>
      )}
    </div>
  )
}
